import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import CustomSelect from 'components/Form/CustomSelect';
import { MaritalStatus } from 'constants/marital-status';
import {
  useGetBeneficiary,
  useUpdateBeneficiary
} from 'common/queries-and-mutations/beneficiary';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import { UpdateBeneficiaryType } from 'apps/user/types/on-boarding.type';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { RelationshipType } from 'constants/bank-account-type';
import { InfoModal } from 'components/Modal';
import GenderField from 'components/Form/GenderField';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  beneficiaryId: number;
};

export default function EditBeneficiaryModal({
  onClose,
  onSuccess,
  beneficiaryId
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useUpdateBeneficiary();

  const { data, isFetched } = useGetBeneficiary({
    beneficiaryId
  });

  const beneficiary = useMemo(() => {
    if (isFetched && data) {
      return {
        beneficiaryFirstName: data.beneficiaryFirstName,
        beneficiaryMiddleName: data.beneficiaryMiddleName,
        beneficiaryLastName: data.beneficiaryLastName,
        beneficiaryType: data.beneficiaryType,
        relationship: data.relationship,
        address: data.address,
        emailAddress: data.emailAddress,
        dateOfBirth: data.dateOfBirth,
        maritalStatus: data.maritalStatus,
        gender: data.gender,
        rcNumber: data.rcNumber,
        incorporationDate: data.incorporationDate,
        incorporationPlace: data.incorporationPlace
      };
    } else {
      return {
        beneficiaryFirstName: '',
        beneficiaryMiddleName: '',
        beneficiaryLastName: '',
        beneficiaryType: '',
        relationship: '',
        address: '',
        emailAddress: '',
        dateOfBirth: '',
        maritalStatus: '',
        gender: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      };
    }
  }, [data, isFetched]);

  const formik = useFormik({
    initialValues: { ...beneficiary },
    validationSchema: Yup.object({
      beneficiaryFirstName: Yup.string().when('beneficiaryType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string()
          .required('Beneficiary first name is required')
          .nullable(),
        otherwise: Yup.string().required('Name is required').nullable()
      }),
      beneficiaryLastName: Yup.string().when('beneficiaryType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string()
          .required('Beneficiary last name is required')
          .nullable(),
        otherwise: Yup.string()
      }),
      beneficiaryType: Yup.string()
        .required('Beneficiary type is required')
        .nullable(),
      rcNumber: Yup.string().when('beneficiaryType', {
        is: BeneficiaryTypeEnum.CORPORATE,
        then: Yup.string().required('RC Number is required').nullable(),
        otherwise: Yup.string()
      }),
      dateOfBirth: Yup.string().when('beneficiaryType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Date of Birth is required').nullable(),
        otherwise: Yup.string()
      }),
      relationship: Yup.string().when('beneficiaryType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Relationship is required').nullable(),
        otherwise: Yup.string()
      }),
      address: Yup.string().required('Home address is required').nullable(),
      gender: Yup.string().required('Gender is required').nullable(),
      emailAddress: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBeneficarySubmit(values);
    }
  });

  const onBeneficarySubmit = (formValue: UpdateBeneficiaryType) => {
    mutate({ beneficiaryId, update: formValue });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(
        <Alert message="Beneficiary Successfully Updated" type="success" />
      );
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Edit Beneficiary</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          <div className="mb-1 mt-8">
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`beneficiaryFirstName`}
                  placeholder=""
                  value={formik.values.beneficiaryFirstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={
                    beneficiary.beneficiaryType ===
                    BeneficiaryTypeEnum.INDIVIDUAL
                      ? 'Beneficiary First Name'
                      : 'Name'
                  }
                  error={
                    formik.touched.beneficiaryFirstName
                      ? formik.errors.beneficiaryFirstName
                      : ''
                  }
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <div className="relative mb-2 flex items-center justify-between">
                  <span className="flex items-center">
                    <label className="text-[12px] font-medium text-[#353535]">
                      Beneficiary Type
                    </label>
                    <div className="mx-2 mb-2">
                      <Spacer width={3} />
                      <Icon name={Icons.Required} />
                    </div>
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-10">
                  <button
                    type="button"
                    disabled
                    onClick={() =>
                      formik.setFieldValue(
                        `beneficiaryType`,
                        BeneficiaryTypeEnum.INDIVIDUAL
                      )
                    }
                    className="flex cursor-not-allowed items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Individual
                    </span>
                    <Icon
                      name={
                        beneficiary.beneficiaryType ===
                        BeneficiaryTypeEnum.INDIVIDUAL
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                  <button
                    type="button"
                    disabled
                    onClick={() =>
                      formik.setFieldValue(
                        `beneficiaryType`,
                        BeneficiaryTypeEnum.CORPORATE
                      )
                    }
                    className="flex cursor-not-allowed items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Corporate
                    </span>
                    <Icon
                      name={
                        beneficiary.beneficiaryType ===
                        BeneficiaryTypeEnum.CORPORATE
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
            {beneficiary.beneficiaryType === BeneficiaryTypeEnum.INDIVIDUAL && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`beneficiaryMiddleName`}
                    placeholder=""
                    value={formik.values.beneficiaryMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Beneficiary Middle Name"
                    error={
                      formik.touched.beneficiaryMiddleName
                        ? formik.errors.beneficiaryMiddleName
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`beneficiaryLastName`}
                    placeholder=""
                    value={formik.values.beneficiaryLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Beneficiary Last Name"
                    error={
                      formik.touched.beneficiaryMiddleName
                        ? formik.errors.beneficiaryMiddleName
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            {formik.values.beneficiaryType ===
              BeneficiaryTypeEnum.INDIVIDUAL && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={RelationshipType}
                    selectedOption={{
                      value: formik.values.relationship,
                      label: formik.values.relationship
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(`relationship`, option?.value);
                    }}
                    label="Relationship"
                    name="relationship"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.relationship
                        ? formik.errors.relationship
                        : ''
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`address`}
                    placeholder=""
                    isRequired
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Home Address"
                    error={formik.touched.address ? formik.errors.address : ''}
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            {formik.values.beneficiaryType ===
              BeneficiaryTypeEnum.CORPORATE && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`rcNumber`}
                    placeholder=""
                    value={formik.values.rcNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="RC Number"
                    isRequired
                    error={
                      formik.touched.rcNumber ? formik.errors.rcNumber : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`address`}
                    placeholder=""
                    isRequired
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Company Address"
                    error={formik.touched.address ? formik.errors.address : ''}
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.beneficiaryType && (
                  <TextField
                    name={`emailAddress`}
                    placeholder=""
                    isRequired
                    value={formik.values.emailAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email Address"
                    error={
                      formik.touched.emailAddress
                        ? formik.errors.emailAddress
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.beneficiaryType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <TextField
                    name={`dateOfBirth`}
                    type="date"
                    isRequired
                    placeholder=""
                    value={formik.values.dateOfBirth}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Date of Birth"
                    error={
                      formik.touched.dateOfBirth
                        ? formik.errors.dateOfBirth
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
                {formik.values.beneficiaryType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <TextField
                    name={`incorporationDate`}
                    type="date"
                    placeholder=""
                    value={formik.values.incorporationDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Date of Incorporation"
                    error={
                      formik.touched.incorporationDate
                        ? formik.errors.incorporationDate
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.beneficiaryType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <CustomSelect
                    options={MaritalStatus}
                    selectedOption={{
                      value: formik.values.maritalStatus,
                      label: formik.values.maritalStatus
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(`maritalStatus`, option?.value);
                    }}
                    label="Marital Status"
                    name="maritalStatus"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.maritalStatus
                        ? formik.errors.maritalStatus
                        : ''
                    }
                  />
                )}
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <GenderField
                  gender={formik.values.gender}
                  onSelect={gender => formik.setFieldValue(`gender`, gender)}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.beneficiaryType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <TextField
                    name={`incorporationPlace`}
                    placeholder=""
                    value={formik.values.incorporationPlace}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Place of Incorporation"
                    error={
                      formik.touched.incorporationPlace
                        ? formik.errors.incorporationPlace
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[12rem] rounded p-[0.1rem]"
              label="Update Beneficiary"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
